export enum DocumentGroup {
  IDENTITY_IMMIGRATION = "Identity & Immigration History",
  EDUCATION_ACADEMIC = "Education & Academic Background",
  EMPLOYMENT_HISTORY = "Employment History",
  FINANCIAL_CAPACITY = "Financial Capacity & Sponsorship",
  FAMILY_DEPENDENTS = "Family, Partner & Dependents",
  ASSETS_HOME_COUNTRY = "Assets & Ties to Home Country",
  HEALTH_INSURANCE = "Health & Insurance",
  APPLICATION_GS = "Application & Genuine Student",
}

export enum DocumentFolder {
  IDENTITY = "identityDocuments",
  ACADEMIC = "academicDocuments",
  EMPLOYMENT = "employmentDocuments",
  FINANCIAL = "financialDocuments",
  FAMILY = "familyDocuments",
  ASSETS = "assetDocuments",
  HEALTH = "healthInsurance",
  APPLICATION = "applicationDocuments",
}

export enum DocumentType {
  // Identity & Immigration
  BIRTH_CERTIFICATE = "BIRTH_CERTIFICATE",
  FAMILY_REGISTER = "FAMILY_REGISTER",
  RESUME = "RESUME",
  PASSPORT = "PASSPORT",
  CURRENT_VISA = "CURRENT_VISA",
  ALL_VISA_COPIES = "ALL_VISA_COPIES",
  VISA_REFUSAL_LETTERS = "VISA_REFUSAL_LETTERS",
  VISA_CANCELLATION_NOTICE = "VISA_CANCELLATION_NOTICE",

  // Education
  ENGLISH_PROFICIENCY_CERTIFICATE = "ENGLISH_PROFICIENCY_CERTIFICATE",
  HIGH_SCHOOL_CERTIFICATE = "HIGH_SCHOOL_CERTIFICATE",
  HIGH_SCHOOL_TRANSCRIPT = "HIGH_SCHOOL_TRANSCRIPT",
  TERTIARY_CERTIFICATE_1 = "TERTIARY_CERTIFICATE_1",
  TERTIARY_CERTIFICATE_2 = "TERTIARY_CERTIFICATE_2",
  TERTIARY_CERTIFICATE_3 = "TERTIARY_CERTIFICATE_3",
  TERTIARY_TRANSCRIPT_1 = "TERTIARY_TRANSCRIPT_1",
  TERTIARY_TRANSCRIPT_2 = "TERTIARY_TRANSCRIPT_2",
  TERTIARY_TRANSCRIPT_3 = "TERTIARY_TRANSCRIPT_3",
  PREVIOUS_ENROLLMENT_LETTER = "PREVIOUS_ENROLLMENT_LETTER",
  PREVIOUS_PROGRAM_TRANSCRIPT = "PREVIOUS_PROGRAM_TRANSCRIPT",

  // Employment
  EMPLOYMENT_LETTER = "EMPLOYMENT_LETTER",
  EMPLOYMENT_1_REFERENCE = "EMPLOYMENT_1_REFERENCE",
  EMPLOYMENT_2_REFERENCE = "EMPLOYMENT_2_REFERENCE",
  EMPLOYMENT_3_REFERENCE = "EMPLOYMENT_3_REFERENCE",
  EMPLOYER_CORRESPONDENCE = "EMPLOYER_CORRESPONDENCE",
  PAYSLIPS = "PAYSLIPS",
  TRAINING_CERTIFICATES = "TRAINING_CERTIFICATES",

  // Financial
  BANK_STATEMENTS = "BANK_STATEMENTS",
  LOAN_DOCUMENTS = "LOAN_DOCUMENTS",
  SCHOLARSHIP_DOCUMENTS = "SCHOLARSHIP_DOCUMENTS",
  SPONSOR_IDENTITY = "SPONSOR_IDENTITY",
  RELATIONSHIP_EVIDENCE = "RELATIONSHIP_EVIDENCE",
  SPONSOR_LETTER = "SPONSOR_LETTER",
  SPONSOR_CITIZENSHIP = "SPONSOR_CITIZENSHIP",
  SPONSOR_VISA = "SPONSOR_VISA",
  SPONSOR_INCOME_TAX = "SPONSOR_INCOME_TAX",
  MULTIPLE_SPONSOR_BREAKDOWN = "MULTIPLE_SPONSOR_BREAKDOWN",
  INCOME_TAX = "INCOME_TAX",

  // Family
  MARRIAGE_CERTIFICATE = "MARRIAGE_CERTIFICATE",
  JOINT_BANK_STATEMENTS = "JOINT_BANK_STATEMENTS",
  JOINT_LEASE = "JOINT_LEASE",
  JOINT_UTILITY_BILLS = "JOINT_UTILITY_BILLS",
  RELATIONSHIP_PHOTOS = "RELATIONSHIP_PHOTOS",
  STATUTORY_DECLARATIONS = "STATUTORY_DECLARATIONS",
  DEPENDENT_PASSPORT = "DEPENDENT_PASSPORT",
  DEPENDENT_BIRTH_CERTIFICATE = "DEPENDENT_BIRTH_CERTIFICATE",
  DEPENDENT_ACADEMICS = "DEPENDENT_ACADEMICS",
  DEPENDENT_EMPLOYMENT = "DEPENDENT_EMPLOYMENT",

  // Assets
  PROPERTY_DEED = "PROPERTY_DEED",
  PROPERTY_TAX_DOCUMENT = "PROPERTY_TAX_DOCUMENT",
  PROPERTY_VALUATION = "PROPERTY_VALUATION",
  LEASE_AGREEMENT = "LEASE_AGREEMENT",
  BUSINESS_REGISTRATION_CERTIFICATE = "BUSINESS_REGISTRATION_CERTIFICATE",
  COMMUNITY_MEMBERSHIP_CERTIFICATE = "COMMUNITY_MEMBERSHIP_CERTIFICATE",
  COMMUNITY_LETTER_OF_APPOINTMENT = "COMMUNITY_LETTER_OF_APPOINTMENT",

  // Health
  HEALTH_INSURANCE_EVIDENCE = "HEALTH_INSURANCE_EVIDENCE",
  OSHC_RECEIPT = "OSHC_RECEIPT",
  DEPENDENT_OSHC = "DEPENDENT_OSHC",

  // Application & GS
  CREDIT_TRANSFER = "CREDIT_TRANSFER",
  RESEARCH_PROPOSAL = "RESEARCH_PROPOSAL",
  PROVIDER_CORRESPONDENCE = "PROVIDER_CORRESPONDENCE",
  STATEMENT_OF_PURPOSE = "STATEMENT_OF_PURPOSE",
  CONDITIONAL_OFFER_LETTER = "CONDITIONAL_OFFER_LETTER",
  UNCONDITIONAL_OFFER_LETTER = "UNCONDITIONAL_OFFER_LETTER",
}

export const DOCUMENT_GROUPS: Record<
  DocumentGroup,
  { folder: DocumentFolder; types: DocumentType[] }
> = {
  [DocumentGroup.IDENTITY_IMMIGRATION]: {
    folder: DocumentFolder.IDENTITY,
    types: [
      DocumentType.BIRTH_CERTIFICATE,
      DocumentType.FAMILY_REGISTER,
      DocumentType.RESUME,
      DocumentType.PASSPORT,
      DocumentType.CURRENT_VISA,
      DocumentType.ALL_VISA_COPIES,
      DocumentType.VISA_REFUSAL_LETTERS,
      DocumentType.VISA_CANCELLATION_NOTICE,
    ],
  },
  [DocumentGroup.EDUCATION_ACADEMIC]: {
    folder: DocumentFolder.ACADEMIC,
    types: [
      DocumentType.ENGLISH_PROFICIENCY_CERTIFICATE,
      DocumentType.HIGH_SCHOOL_CERTIFICATE,
      DocumentType.HIGH_SCHOOL_TRANSCRIPT,
      DocumentType.TERTIARY_CERTIFICATE_1,
      DocumentType.TERTIARY_CERTIFICATE_2,
      DocumentType.TERTIARY_CERTIFICATE_3,
      DocumentType.TERTIARY_TRANSCRIPT_1,
      DocumentType.TERTIARY_TRANSCRIPT_2,
      DocumentType.TERTIARY_TRANSCRIPT_3,
      DocumentType.PREVIOUS_ENROLLMENT_LETTER,
      DocumentType.PREVIOUS_PROGRAM_TRANSCRIPT,
    ],
  },
  [DocumentGroup.EMPLOYMENT_HISTORY]: {
    folder: DocumentFolder.EMPLOYMENT,
    types: [
      DocumentType.EMPLOYMENT_LETTER,
      DocumentType.EMPLOYMENT_1_REFERENCE,
      DocumentType.EMPLOYMENT_2_REFERENCE,
      DocumentType.EMPLOYMENT_3_REFERENCE,
      DocumentType.EMPLOYER_CORRESPONDENCE,
      DocumentType.PAYSLIPS,
      DocumentType.TRAINING_CERTIFICATES,
    ],
  },
  [DocumentGroup.FINANCIAL_CAPACITY]: {
    folder: DocumentFolder.FINANCIAL,
    types: [
      DocumentType.BANK_STATEMENTS,
      DocumentType.LOAN_DOCUMENTS,
      DocumentType.SCHOLARSHIP_DOCUMENTS,
      DocumentType.SPONSOR_IDENTITY,
      DocumentType.RELATIONSHIP_EVIDENCE,
      DocumentType.SPONSOR_LETTER,
      DocumentType.SPONSOR_CITIZENSHIP,
      DocumentType.SPONSOR_VISA,
      DocumentType.SPONSOR_INCOME_TAX,
      DocumentType.MULTIPLE_SPONSOR_BREAKDOWN,
      DocumentType.INCOME_TAX,
    ],
  },
  [DocumentGroup.FAMILY_DEPENDENTS]: {
    folder: DocumentFolder.FAMILY,
    types: [
      DocumentType.MARRIAGE_CERTIFICATE,
      DocumentType.JOINT_BANK_STATEMENTS,
      DocumentType.JOINT_LEASE,
      DocumentType.JOINT_UTILITY_BILLS,
      DocumentType.RELATIONSHIP_PHOTOS,
      DocumentType.STATUTORY_DECLARATIONS,
      DocumentType.DEPENDENT_PASSPORT,
      DocumentType.DEPENDENT_BIRTH_CERTIFICATE,
      DocumentType.DEPENDENT_ACADEMICS,
      DocumentType.DEPENDENT_EMPLOYMENT,
    ],
  },
  [DocumentGroup.ASSETS_HOME_COUNTRY]: {
    folder: DocumentFolder.ASSETS,
    types: [
      DocumentType.PROPERTY_DEED,
      DocumentType.PROPERTY_TAX_DOCUMENT,
      DocumentType.PROPERTY_VALUATION,
      DocumentType.LEASE_AGREEMENT,
      DocumentType.BUSINESS_REGISTRATION_CERTIFICATE,
      DocumentType.COMMUNITY_MEMBERSHIP_CERTIFICATE,
      DocumentType.COMMUNITY_LETTER_OF_APPOINTMENT,
    ],
  },
  [DocumentGroup.HEALTH_INSURANCE]: {
    folder: DocumentFolder.HEALTH,
    types: [
      DocumentType.HEALTH_INSURANCE_EVIDENCE,
      DocumentType.OSHC_RECEIPT,
      DocumentType.DEPENDENT_OSHC,
    ],
  },
  [DocumentGroup.APPLICATION_GS]: {
    folder: DocumentFolder.APPLICATION,
    types: [
      DocumentType.CREDIT_TRANSFER,
      DocumentType.RESEARCH_PROPOSAL,
      DocumentType.PROVIDER_CORRESPONDENCE,
      DocumentType.STATEMENT_OF_PURPOSE,
      DocumentType.CONDITIONAL_OFFER_LETTER,
      DocumentType.UNCONDITIONAL_OFFER_LETTER,
    ],
  },
};
